import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAlert } from "react-alert";
import Moment from "moment";

import { confirmAlert } from "react-confirm-alert"; // Import
import "react-confirm-alert/src/react-confirm-alert.css"; // Import css

import { useDispatch, useSelector } from "react-redux";
import {
  getStudentSubscriptions,
  deleteStudentSubscription,
} from "../../../actions/adminSubscriptionActions";

import Loader from "../../Loader";

const ListUserSubscriptions = ({ history }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "expired"
  const alert = useAlert();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getStudentSubscriptions());
  }, [dispatch]);

  const { studentSubscriptions, sloading, isStudentSubDeleted, error } =
    useSelector((state) => state.subscriptions);

  useEffect(() => {
    if (error) {
      alert.error(error);
    }
    if (isStudentSubDeleted) {
      alert.success("Subscription deleted successfully");
    }
  }, [error, isStudentSubDeleted, alert]);

  const handleDelete = (id, studentName) => {
    confirmAlert({
      title: "Confirm Delete",
      message: `Are you sure you want to remove the subscription for ${studentName}?`,
      buttons: [
        {
          label: "Yes",
          onClick: () => dispatch(deleteStudentSubscription(id)),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  // Helper function to check if subscription is active
  const isSubscriptionActive = (item) => {
    return item?.status === true && new Date(item?.expireAt) > new Date();
  };

  // Filter subscriptions based on search term, status filter, and exclude items with null user
  const filteredSubscriptions = Array.isArray(studentSubscriptions)
    ? studentSubscriptions
        .filter((item) => item && item.user) // Filter out null items and items with null user
        .filter((item) => {
          // Status filter
          if (statusFilter === "active") {
            return isSubscriptionActive(item);
          } else if (statusFilter === "expired") {
            return !isSubscriptionActive(item);
          }
          return true; // "all" - show everything
        })
        .filter((item) => {
          const studentName = `${item?.user?.first_name || ""} ${item?.user?.last_name || ""}`.toLowerCase();
          const studentEmail = (item?.user?.email || "").toLowerCase();
          const examType = (item?.exam?.title || "").toLowerCase();
          const searchLower = searchTerm.toLowerCase();
          return studentName.includes(searchLower) || studentEmail.includes(searchLower) || examType.includes(searchLower);
        })
        // Sort by subscribedAt descending (newest first)
        .sort((a, b) => {
          const dateA = a?.subscribedAt ? new Date(a.subscribedAt).getTime() : (a?.createdAt ? new Date(a.createdAt).getTime() : 0);
          const dateB = b?.subscribedAt ? new Date(b.subscribedAt).getTime() : (b?.createdAt ? new Date(b.createdAt).getTime() : 0);
          return dateB - dateA;
        })
    : [];

  // Pagination logic
  const totalItems = filteredSubscriptions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSubscriptions.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            Manage
            <small>Student Subscriptions</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li className="active">Student Subscriptions</li>
          </ol>
        </section>

        <section className="content">
          <div className="row">
            <div className="col-xs-12">
              <div className="box">
                <div className="box-header">
                  <h3 className="box-title">Student Subscriptions</h3>
                  <div className="box-tools">
                    <select
                      className="form-control input-sm"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{ width: "120px", display: "inline-block", marginRight: "10px" }}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                    </select>
                    <div className="input-group input-group-sm" style={{ width: "300px", display: "inline-flex", marginRight: "10px" }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name, email or exam..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <span className="input-group-btn">
                        <button
                          type="button"
                          className="btn btn-info btn-flat"
                          onClick={() => setSearchTerm("")}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </span>
                    </div>
                    <Link
                      to="/admin/student-subscriptions/add"
                      className="btn btn-success btn-sm"
                    >
                      + Add Subscription
                    </Link>
                  </div>
                </div>
                <div className="box-body table-responsive no-padding">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Email</th>
                        <th>Exam Type</th>
                        <th>Subscription Type</th>
                        <th>Duration</th>
                        <th>Price</th>
                        <th>Subscribed At</th>
                        <th>Expiration</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sloading ? (
                        <tr>
                          <td colSpan="10">
                            <Loader
                              type="Puff"
                              color="#00BFFF"
                              height={50}
                              width={50}
                            />
                          </td>
                        </tr>
                      ) : currentItems.length ? (
                        currentItems.map((item) => (
                          <tr key={item._id}>
                            <td>
                              {item?.user?.first_name} {item?.user?.last_name}
                            </td>
                            <td>{item?.user?.email}</td>
                            <td>{item?.exam?.title}</td>
                            <td>{item?.subscriptionType}</td>
                            <td>{item?.duration}</td>
                            <td>₦{item?.price}</td>
                            <td>
                              {Moment(item?.subscribedAt).format("YYYY-MM-DD")}
                            </td>
                            <td>
                              {Moment(item?.expireAt).format("YYYY-MM-DD")}
                            </td>

                            <td>
                              {isSubscriptionActive(item) ? (
                                <span className="badge bg-green">Active</span>
                              ) : (
                                <span className="badge bg-yellow">Expired</span>
                              )}
                            </td>

                            <td>
                              <button
                                className="btn btn-danger btn-xs"
                                onClick={() =>
                                  handleDelete(
                                    item._id,
                                    `${item?.user?.first_name} ${item?.user?.last_name}`
                                  )
                                }
                              >
                                <i className="fa fa-trash"></i> Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="10" style={{ textAlign: "center" }}>
                            {searchTerm ? "No matching records found" : "No records found"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot></tfoot>
                  </table>
                </div>
                {/* Pagination */}
                {!sloading && filteredSubscriptions.length > 0 && (
                  <div className="box-footer clearfix">
                    <div className="row">
                      <div className="col-sm-4">
                        <div className="dataTables_info">
                          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} entries
                        </div>
                      </div>
                      <div className="col-sm-4 text-center">
                        <div className="form-inline">
                          <label style={{ marginRight: "10px" }}>Show</label>
                          <select
                            className="form-control input-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <label style={{ marginLeft: "10px" }}>entries</label>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <ul className="pagination pagination-sm no-margin pull-right">
                          <li className={currentPage === 1 ? "disabled" : ""}>
                            <button
                              onClick={() => handlePageChange(1)}
                              disabled={currentPage === 1}
                              style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                            >
                              «
                            </button>
                          </li>
                          <li className={currentPage === 1 ? "disabled" : ""}>
                            <button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                            >
                              ‹
                            </button>
                          </li>
                          {getPageNumbers().map((page) => (
                            <li key={page} className={currentPage === page ? "active" : ""}>
                              <button onClick={() => handlePageChange(page)}>{page}</button>
                            </li>
                          ))}
                          <li className={currentPage === totalPages ? "disabled" : ""}>
                            <button
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              style={{ cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
                            >
                              ›
                            </button>
                          </li>
                          <li className={currentPage === totalPages ? "disabled" : ""}>
                            <button
                              onClick={() => handlePageChange(totalPages)}
                              disabled={currentPage === totalPages}
                              style={{ cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
                            >
                              »
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
};

export default ListUserSubscriptions;
